import { orderBy, query, Unsubscribe } from 'firebase/firestore';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { FrequencyConfig, HabitConfig, TargetConfig } from '../config/habit.config';
import { useAuthContext } from '../contexts/auth.context';
import { New } from '../dto/entity';
import { Habit, HabitCollection, HabitConverter, HabitFrequency } from '../dto/habit';
import {
  HabitFrequencyType,
  HabitTarget,
  HabitTargetType,
  SpecialDays,
  TargetDirection,
  TargetEvaluation,
} from '../dto/habit.enum';
import { User, UserCollection } from '../dto/user';
import { useDataStore } from '../stores/data.store';
import { getDay, removeNullFields } from '../util/util';
import { useFirebase } from './firebase.hook';
import { useRecord } from './record.hook';

export interface HabitInterface {
  loadHabits(user: User): Unsubscribe;
  resetHabits(): void;
  habitsLoading: boolean;
  habits: Habit[];

  startHabit: (newHabit: HabitConfig) => Promise<Habit | undefined>;
  editHabit: (habit: Habit, update: Partial<Habit>) => Promise<Habit | undefined>;
  endHabit: (habit: Habit) => Promise<Habit | undefined>;
  resetHabit: (habit: Habit) => Promise<Habit | undefined>;
  deleteHabit: (habit: Habit) => Promise<void>;
  deleteHabits: () => Promise<void>;

  habitsForPartner: (user: User) => Promise<Habit[]>;
}

export function useHabit(): HabitInterface {
  const { user } = useAuthContext();
  const { setDocument, deleteDocument, collection, queryRun, querySubscribe, deleteQuery, waitIfOnline } =
    useFirebase();
  const { deleteRecords } = useRecord();

  const [storeHabits, setStoreHabits] = useDataStore(useShallow((s) => [s.habits, s.setHabits]));

  // DB methods
  function loadHabits(user: User): Unsubscribe {
    return querySubscribe<Habit>(
      query(
        collection(`${UserCollection}/${user.id}/${HabitCollection}`).withConverter(HabitConverter),
        orderBy('start', 'asc'),
      ),
      setStoreHabits,
    );
  }

  function resetHabits() {
    setStoreHabits(undefined);
  }

  // user methods
  async function startHabit(newHabit: HabitConfig): Promise<Habit | undefined> {
    if (!user) return;

    const habit: New<Habit> = {
      ...removeNullFields({
        title: newHabit.title,
        notes: newHabit.notes,
        start: getDay(new Date()),
        target: createTarget(newHabit.targetType, newHabit.targetConfig ?? {}),
        frequency: createFrequency(newHabit.frequencyType, newHabit.frequencyConfig ?? {}),
      }),
      end: null,
    };

    return waitIfOnline(setDocument(`${UserCollection}/${user.id}/${HabitCollection}`, habit));
  }

  async function editHabit(habit: Habit, update: Partial<Habit>): Promise<Habit | undefined> {
    if (!user) return;

    habit.title = update.title ?? habit.title;
    habit.notes = update.notes ?? habit.notes;
    habit.target = update.target ?? habit.target;

    return waitIfOnline(setDocument(`${UserCollection}/${user.id}/${HabitCollection}`, habit, habit.id));
  }

  async function endHabit(habit: Habit): Promise<Habit | undefined> {
    if (!user) return;

    habit.end = getDay(new Date());

    return waitIfOnline(setDocument(`${UserCollection}/${user.id}/${HabitCollection}`, habit, habit.id));
  }

  async function resetHabit(habit: Habit): Promise<Habit | undefined> {
    if (!user) return;

    habit.start = getDay(new Date());

    await waitIfOnline(
      Promise.all([
        setDocument(`${UserCollection}/${user.id}/${HabitCollection}`, habit, habit.id),
        deleteRecords(user, habit),
      ]),
    );

    return habit;
  }

  async function deleteHabit(habit: Habit): Promise<void> {
    if (!user) return;

    await waitIfOnline(
      Promise.all([
        deleteDocument(`${UserCollection}/${user.id}/${HabitCollection}`, habit.id),
        deleteRecords(user, habit),
      ]),
    );
  }

  async function deleteHabits(): Promise<void> {
    if (!user) return;

    await Promise.all([deleteQuery(collection(UserCollection, user.id, HabitCollection)), deleteRecords(user)]);
  }

  async function habitsForPartner(partner: User): Promise<Habit[]> {
    return queryRun<Habit>(
      query(collection(UserCollection, partner.id, HabitCollection).withConverter(HabitConverter)),
    );
  }

  // helper methods
  function createTarget(type: HabitTargetType, { value, evaluation, direction }: TargetConfig): HabitTarget {
    switch (type) {
      case HabitTargetType.BOOLEAN:
        return { type, value: 1, evaluation: TargetEvaluation.DAILY, direction: TargetDirection.GROWTH };

      case HabitTargetType.QUANTITY:
        if (value == null) throw new Error('Missing target');
        if (evaluation == null) throw new Error('Missing target evaluation');
        if (direction == null) throw new Error('Missing target direction');
        return { type, value, evaluation, direction };

      case HabitTargetType.RATING:
        return { type, value: 10, evaluation: TargetEvaluation.DAILY, direction: TargetDirection.GROWTH };
    }
  }

  function createFrequency(
    type: HabitFrequencyType,
    { count, weekDays, monthDays, specialDays }: FrequencyConfig,
  ): HabitFrequency {
    switch (type) {
      case HabitFrequencyType.DAILY:
        return { type, count: 1 };

      case HabitFrequencyType.WEEKLY:
        if (count && count > 7) throw new Error('Invalid count');
        return { type, count: count ?? 1 };

      case HabitFrequencyType.MONTHLY:
        if (count && count > 31) throw new Error('Invalid count');
        return { type, count: count ?? 1 };

      case HabitFrequencyType.WEEK_DAYS:
        if (!weekDays?.length) throw new Error('Missing week days');
        return removeNullFields({ type, count: weekDays.length, weekDays });

      case HabitFrequencyType.MONTH_DAYS:
        if (!monthDays?.length) throw new Error('Missing month days');
        if (monthDays.some((d) => d < 1 || d > 31)) throw new Error('Invalid month days');
        return removeNullFields({ type, count: monthDays.length, monthDays });

      case HabitFrequencyType.SPECIAL_DAYS:
        if (!specialDays) throw new Error('Missing special days');
        return removeNullFields({ type, count: specialDays === SpecialDays.EMBER ? 12 : 1, specialDays });
    }
  }

  return useMemo(
    () => ({
      loadHabits,
      resetHabits,
      habitsLoading: user != null && !storeHabits,
      habits: storeHabits ?? [],
      startHabit,
      editHabit,
      endHabit,
      resetHabit,
      deleteHabit,
      deleteHabits,
      habitsForPartner,
    }),
    [storeHabits, user, setDocument, deleteDocument, collection, queryRun, querySubscribe, deleteQuery, waitIfOnline],
  );
}
